import React, { useEffect, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  Pencil,
  Check,
  Plus,
  PlusCircle,
  Eye,
  GripVertical,
  TrendingUp,
  TrendingDown,
  Wand2,
  ScanSearch,
  ShieldCheck,
  Pin,
  RotateCcw,
  Settings2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, money, dateFmt } from "../lib/api.js";
import { Card, Empty, Pill, Modal } from "../components/UI.jsx";
import SystemDashboard from "./SystemDashboard.jsx";
import { t } from "../lib/i18n.js";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const Grid = WidthProvider(Responsive);
const safeJSON = (value, fallback) => {
  try {
    return JSON.parse(value) || fallback;
  } catch {
    return fallback;
  }
};
const defaultTabs = ["Resume", "Cash flow", "Net worth"];
const defaultCards = {
  Resume: [
    "balance",
    "currencybalances",
    "cashflow",
    "passive",
    "networth",
    "incomequadrant",
    "rd",
    "budget",
    "comparison",
    "projects",
    "quality",
  ],
  "Cash flow": [
    "cashflow",
    "incomeexpense",
    "comparison",
    "savingsrate",
    "budget",
    "review",
  ],
  "Net worth": [
    "networth",
    "networthevolution",
    "periodcomparison",
    "balance",
    "accounts",
    "investments",
    "reconciliation",
    "quality",
  ],
};
const meta = {
  balance: { title: "Total balance", w: 3, h: 2, minW: 2, minH: 2 },
  cashflow: { title: "Cash flow", w: 6, h: 4, minW: 4, minH: 3 },
  passive: { title: "Passive income YTD", w: 3, h: 2, minW: 2, minH: 2 },
  networth: { title: "Net worth", w: 3, h: 2, minW: 2, minH: 2 },
  networthevolution: {
    title: "Net worth evolution",
    w: 6,
    h: 4,
    minW: 4,
    minH: 3,
  },
  periodcomparison: {
    title: "Period comparison",
    w: 4,
    h: 3,
    minW: 3,
    minH: 2,
  },
  rd: { title: "Cash-flow quadrant", w: 4, h: 3, minW: 3, minH: 3 },
  budget: { title: "50 / 30 / 20", w: 6, h: 4, minW: 4, minH: 3 },
  fx: { title: "Currency rates", w: 3, h: 4, minW: 2, minH: 3 },
  accounts: { title: "Account balances", w: 4, h: 4, minW: 3, minH: 3 },
  accountspotlight: {
    title: "Individual account",
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  placesbalances: { title: "Places overview", w: 5, h: 4, minW: 4, minH: 3 },
  balanceevolution: {
    title: "Account balance evolution",
    w: 6,
    h: 4,
    minW: 4,
    minH: 3,
  },
  currencybalances: {
    title: "Balances by currency",
    w: 5,
    h: 4,
    minW: 4,
    minH: 3,
  },
  comparison: { title: "Previous period", w: 3, h: 3, minW: 2, minH: 2 },
  projects: { title: "Projects", w: 4, h: 3, minW: 3, minH: 2 },
  budgets: { title: "Budgets", w: 4, h: 3, minW: 3, minH: 2 },
  goals: { title: "Goals", w: 4, h: 3, minW: 3, minH: 2 },
  subscriptions: { title: "Subscriptions", w: 4, h: 3, minW: 3, minH: 2 },
  investments: { title: "Investments", w: 4, h: 3, minW: 3, minH: 2 },
  assetmix: { title: "Assets by type", w: 5, h: 4, minW: 4, minH: 3 },
  assetshowcase: { title: "Asset showcase", w: 5, h: 4, minW: 4, minH: 3 },
  review: { title: "Needs review", w: 3, h: 2, minW: 2, minH: 2 },
  quality: { title: "Data quality", w: 4, h: 3, minW: 3, minH: 3 },
  savingsrate: { title: "Savings rate", w: 3, h: 2, minW: 2, minH: 2 },
  incomeexpense: { title: "Income vs expenses", w: 4, h: 3, minW: 3, minH: 2 },
  reconciliation: { title: "Reconciliation", w: 4, h: 3, minW: 3, minH: 2 },
  recent: { title: "Recent movements", w: 5, h: 4, minW: 4, minH: 3 },
};
const unitSizes = {
  balance: [1, 1],
  cashflow: [2, 2],
  passive: [1, 1],
  networth: [1, 1],
  networthevolution: [2, 2],
  periodcomparison: [1, 2],
  rd: [1, 2],
  incomequadrant: [2, 2],
  budget: [2, 2],
  fx: [1, 2],
  accounts: [1, 2],
  accountspotlight: [1, 2],
  placesbalances: [2, 2],
  balanceevolution: [2, 2],
  currencybalances: [2, 2],
  comparison: [1, 1],
  projects: [1, 2],
  budgets: [1, 2],
  goals: [1, 2],
  subscriptions: [1, 2],
  investments: [1, 1],
  assetmix: [2, 2],
  assetshowcase: [2, 2],
  review: [1, 1],
  quality: [1, 2],
  savingsrate: [1, 1],
  incomeexpense: [1, 2],
  reconciliation: [1, 2],
  recent: [2, 2],
};
for (const [id, size] of Object.entries(unitSizes)) {
  meta[id] = meta[id] || { title: id };
  Object.assign(meta[id], {
    w: size[0],
    h: size[1],
    minW: 1,
    minH: 1,
    maxW: 3,
    maxH: 3,
  });
}
meta.incomequadrant.title = "Income source quadrant";
const breakpoints = { lg: 1100, md: 800, sm: 500, xs: 0 };
const cols = { lg: 3, md: 2, sm: 1, xs: 1 };
const configurableCards = new Set(["balance","passive","networth","networthevolution","periodcomparison","cashflow","incomequadrant","budget","comparison","projects","savingsrate","incomeexpense"]);

function pack(cards, colCount) {
  let x = 0,
    y = 0,
    rowH = 0;
  return cards.map((id) => {
    const m = meta[id] || { w: 3, h: 3, minW: 2, minH: 2 },
      w = Math.min(colCount, Math.max(1, m.w)),
      h = Math.max(m.minH || 1, m.h || 2);
    if (x + w > colCount) {
      x = 0;
      y += rowH;
      rowH = 0;
    }
    const item = {
      i: id,
      x,
      y,
      w,
      h,
      minW: Math.min(colCount, Math.max(1, m.minW || 1)),
      minH: m.minH || 1,
    };
    x += w;
    rowH = Math.max(rowH, h);
    return item;
  });
}
const buildLayouts = (cards) =>
  Object.fromEntries(Object.entries(cols).map(([k, c]) => [k, pack(cards, c)]));
function normalizeLayouts(raw, cards) {
  if (raw && typeof raw === "object" && !Array.isArray(raw) && raw.lg) {
    const isLegacy = Object.values(raw)
      .flat()
      .some((item) => Number(item.w) > 3 || Number(item.h) > 3);
    if (isLegacy) return buildLayouts(cards);
    return Object.fromEntries(
      Object.entries(cols).map(([key, count]) => [
        key,
        (raw[key] || pack(cards, count))
          .filter((item) => cards.includes(item.i))
          .map((item) => ({
            ...item,
            w: Math.min(count, Math.max(1, Math.round(item.w || 1))),
            h: Math.min(3, Math.max(1, Math.round(item.h || 1))),
            minW: 1,
            minH: 1,
            maxW: Math.min(3, count),
            maxH: 3,
          })),
      ]),
    );
  }
  if (Array.isArray(raw)) return buildLayouts(cards);
  return buildLayouts(cards);
}
function advice(b, total) {
  if (!total)
    return "Classify some expenses to start comparing your spending with the 50/30/20 guide.";
  const p = {
    needs: (b.needs / total) * 100,
    wants: (b.wants / total) * 100,
    goals: (b.goals / total) * 100,
    unclassified: (b.unclassified / total) * 100,
  };
  if (p.unclassified > 15)
    return `${p.unclassified.toFixed(0)}% of expenses are unclassified. Review those first.`;
  if (p.goals < 15)
    return `You are putting about ${p.goals.toFixed(0)}% toward goals/savings. The guide suggests 20%, so this is the area to improve first.`;
  if (p.wants > 35)
    return `Wants are about ${p.wants.toFixed(0)}%. Consider bringing them closer to 30%.`;
  if (p.needs > 55)
    return `Needs are about ${p.needs.toFixed(0)}%. Check recurring fixed costs.`;
  return "Good — your current mix is close to the 50/30/20 guide.";
}

export default function Dashboard({ user, onAddMovement }) {
  const navigate = useNavigate();
  const saved = safeJSON(
      localStorage.getItem(`pisto-dashboard-${user.id}`),
      {},
    ),
    defaultView = saved.defaultView || null;
  const [d, setD] = useState(null),
    [fx, setFx] = useState(null),
    [cfg, setCfg] = useState(null),
    [extras, setExtras] = useState({}),
    [error, setError] = useState(""),
    [edit, setEdit] = useState(false),
    [period, setPeriod] = useState(defaultView?.period || "month"),
    [selectedYears, setSelectedYears] = useState(
      defaultView?.selectedYears || [],
    ),
    [globalCostCenterId, setGlobalCostCenterId] = useState(defaultView?.filters?.costCenterId || ""),
    [cardSettings, setCardSettings] = useState(saved.cardSettings || {}),
    [cardConfig, setCardConfig] = useState(null),
    [scopedData, setScopedData] = useState({});
  const [accountCardId, setAccountCardId] = useState(defaultView?.filters?.accountCardId||""),
    [placeCardName, setPlaceCardName] = useState(defaultView?.filters?.placeCardName||"all"),
    [balanceAccountId, setBalanceAccountId] = useState(defaultView?.filters?.balanceAccountId||"all"),
    [assetTypeFilter, setAssetTypeFilter] = useState(defaultView?.filters?.assetTypeFilter||"all");
  const initialTabs = saved.tabs || defaultTabs;
  const [tabs, setTabs] = useState(
      user.role !== "user" && !initialTabs.includes("System")
        ? [...initialTabs, "System"]
        : initialTabs,
    ),
    [tab, setTab] = useState(
      defaultView?.tab && initialTabs.includes(defaultView.tab)
        ? defaultView.tab
        : "Resume",
    );
  const [cardsByTab, setCardsByTab] = useState({
      ...defaultCards,
      ...(saved.cardsByTab || {}),
    }),
    [layouts, setLayouts] = useState(saved.layouts || {});

  const dashboardUrl = (scope = {}) => {
    const query = new URLSearchParams({ period });
    if (period === "years" && selectedYears.length) query.set("years", selectedYears.join(","));
    const accountIds = scope.accountIds || [];
    const costCenterIds = scope.costCenterIds || (globalCostCenterId ? [globalCostCenterId] : []);
    if (accountIds.length) query.set("accountIds", accountIds.join(","));
    if (costCenterIds.length) query.set("costCenterIds", costCenterIds.join(","));
    return `/dashboard?${query.toString()}`;
  };

  const load = () => {
    setError("");
    Promise.all([
      api(dashboardUrl()),
      api("/fx/latest").catch(() => null),
      api("/config").catch(() => null),
      api("/budgets").catch(() => []),
      api("/goals").catch(() => []),
      api("/subscriptions").catch(() => []),
      api("/assets").catch(() => []),
      api("/reconciliations").catch(() => []),
      api("/transactions?limit=10").catch(() => []),
      api("/data-quality").catch(() => null),
    ])
      .then(
        ([
          dash,
          rates,
          c,
          budgets,
          goals,
          subs,
          assets,
          recs,
          recent,
          quality,
        ]) => {
          setD(dash);
          setFx(rates);
          setCfg(c);
          setExtras({ budgets, goals, subs, assets, recs, recent, quality });
        },
      )
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    if (tab !== "System" && (period !== "years" || selectedYears.length))
      load();
  }, [period, selectedYears, tab, globalCostCenterId]);
  useEffect(() => {
    const h = () => tab !== "System" && load();
    window.addEventListener("pisto:data-changed", h);
    return () => window.removeEventListener("pisto:data-changed", h);
  }, [period, tab, globalCostCenterId]);
  useEffect(() => {
    if (tab === "System" || !cfg) return;
    const entries = (cardsByTab[tab] || []).map((id) => [id, cardSettings[`${tab}:${id}`]]).filter(([, settings]) => settings && settings.useGlobalFilters === false && ((settings.accountIds || []).length || (settings.costCenterIds || []).length));
    if (!entries.length) { setScopedData({}); return; }
    let cancelled = false;
    Promise.all(entries.map(async ([id, settings]) => [id, await api(dashboardUrl(settings))])).then((results) => { if (!cancelled) setScopedData(Object.fromEntries(results)); }).catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [period, selectedYears, tab, globalCostCenterId, cardSettings, cardsByTab, cfg]);
  useEffect(() => {
    localStorage.setItem(
      `pisto-dashboard-${user.id}`,
      JSON.stringify({
        tabs,
        cardsByTab,
        layouts,
        cardSettings,
        defaultView:
          safeJSON(localStorage.getItem(`pisto-dashboard-${user.id}`), {})
            .defaultView || null,
      }),
    );
  }, [tabs, cardsByTab, layouts, cardSettings, user.id]);
  const saveDefault = async () => {
    const value = {
      tab,
      period,
      selectedYears,
      filters: {
        accountCardId,
        placeCardName,
        balanceAccountId,
        assetTypeFilter,
        costCenterId: globalCostCenterId,
      },
    };
    localStorage.setItem(
      `pisto-dashboard-${user.id}`,
      JSON.stringify({ tabs, cardsByTab, layouts, cardSettings, defaultView: value }),
    );
    await api("/preferences/dashboard-default", {
      method: "PUT",
      body: value,
    }).catch(() => {});
  };
  const resetDefault = async () => {
    localStorage.setItem(
      `pisto-dashboard-${user.id}`,
      JSON.stringify({ tabs, cardsByTab, layouts, cardSettings, defaultView: null }),
    );
    await api("/preferences/dashboard-default", {
      method: "PUT",
      body: { reset: true },
    }).catch(() => {});
    setTab("Resume");
    setPeriod("month");
    setSelectedYears([]);
    setGlobalCostCenterId("");
  };

  const maxTabs = Number(cfg?.preferences?.max_dashboard_tabs || 5);
  const addTab = () => {
    if (tabs.filter((x) => x !== "System").length >= maxTabs)
      return alert(
        `Dashboard is limited to ${maxTabs} tabs. Change this in Configuration.`,
      );
    const n = prompt("New dashboard tab name");
    if (n && n.trim() && !tabs.includes(n.trim())) {
      setTabs([...tabs, n.trim()]);
      setCardsByTab((x) => ({ ...x, [n.trim()]: [] }));
      setTab(n.trim());
    }
  };

  if (tab === "System" && ["super_admin", "admin"].includes(user.role))
    return (
      <>
        <div className="dashboard-tabs-row">
          <div className="tabs">
            {tabs.map((name) => (
              <button
                className={tab === name ? "active" : ""}
                onClick={() => setTab(name)}
                key={name}
              >
                {name}
              </button>
            ))}
            <button onClick={addTab}>
              <Plus size={15} /> Tab
            </button>
          </div>
        </div>
        <SystemDashboard />
      </>
    );
  if (error && !d)
    return (
      <div className="error">
        Could not load dashboard: {error}{" "}
        <button className="text-button" onClick={load}>
          Retry
        </button>
      </div>
    );
  if (!d) return <div className="loading">Loading dashboard…</div>;

  const currentCards = cardsByTab[tab] || [],
    currentLayouts = normalizeLayouts(layouts[tab], currentCards);
  const cash = d.cashflow.map((x) => ({
    ...x,
    income: Number(x.income),
    expense: Number(x.expense),
  }));
  const chartTooltip = (value, name) => [
    money(Number(value), d.baseCurrency),
    String(name || "").replaceAll("_", " "),
  ];
  const b = d.budget503020 || { needs: 0, wants: 0, goals: 0, unclassified: 0 },
    bt = Object.values(b).reduce((sum, x) => sum + Number(x || 0), 0);
  const pie = [
    ["Needs", b.needs],
    ["Wants", b.wants],
    ["Goals", b.goals],
    ["Unclassified", b.unclassified],
  ].map(([name, value]) => ({ name, value: Number(value || 0) }));
  const rates = (fx?.rates || [])
    .filter((x) => ["USD", "GBP", "CHF", "MXN"].includes(x.currency))
    .slice(0, 4);
  const savingsRate =
    d.periodIncome > 0
      ? Math.max(-999, Math.min(999, (d.periodNet / d.periodIncome) * 100))
      : 0;
  const fxMap = Object.fromEntries(
    (fx?.rates || []).map((r) => [r.currency, Number(r.eur_rate)]),
  );
  fxMap.EUR = 1;
  const currencyRows = Object.entries(
    (d.accounts || []).reduce((out, a) => {
      const sign = a.is_liability ? -1 : 1;
      out[a.currency] =
        (out[a.currency] || 0) +
        sign * Math.abs(Number(a.current_balance || 0));
      return out;
    }, {}),
  )
    .map(([currency, native]) => ({
      currency,
      native,
      converted:
        fxMap[currency] && fxMap[d.baseCurrency]
          ? (native / fxMap[currency]) * fxMap[d.baseCurrency]
          : null,
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
  const selectedAccount =
    (d.accounts || []).find((a) => String(a.id) === String(accountCardId)) ||
    d.accounts?.[0];
  const selectedPlace =
    placeCardName === "all"
      ? null
      : (d.places || []).find((x) => x.name === placeCardName);
  const historySeries =
    balanceAccountId === "all"
      ? d.balanceHistory?.all || []
      : (d.balanceHistory?.accounts || [])
          .find((x) => String(x.id) === String(balanceAccountId))
          ?.points?.map((x) => ({ d: x.d, value: x.reporting })) || [];
  const assetTypes = [
    ...new Set((extras.assets || []).map((x) => x.asset_type)),
  ].sort();
  const assetValueInBase = (asset) => {
    const native = Number(
      asset.current_value ??
        Number(asset.quantity || 1) * Number(asset.current_price || 0),
    );
    return fxMap[asset.currency] && fxMap[d.baseCurrency]
      ? (native / fxMap[asset.currency]) * fxMap[d.baseCurrency]
      : null;
  };
  const assetGroups = Object.values(
    (extras.assets || []).reduce((out, asset) => {
      const key = asset.asset_type || "other",
        converted = assetValueInBase(asset);
      out[key] = out[key] || {
        name: key.replaceAll("_", " "),
        value: 0,
        count: 0,
      };
      out[key].count++;
      if (converted != null)
        out[key].value += asset.is_liability ? -Math.abs(converted) : converted;
      return out;
    }, {}),
  ).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const showcaseAssets = (extras.assets || [])
    .filter(
      (x) => assetTypeFilter === "all" || x.asset_type === assetTypeFilter,
    )
    .sort(
      (a, b) =>
        Math.abs(assetValueInBase(b) || 0) - Math.abs(assetValueInBase(a) || 0),
    );
  const incomeSources = d.incomeSources || {},
    classifiedIncomeSourceKeys = [
      "employee",
      "self_employed",
      "business_owner",
      "investor",
    ],
    incomeSourceTotal = classifiedIncomeSourceKeys.reduce(
      (sum, key) => sum + Number(incomeSources[key] || 0),
      0,
    ),
    dominantIncomeSource = Object.entries(incomeSources)
      .filter(([key]) => classifiedIncomeSourceKeys.includes(key))
      .sort((a, b) => Number(b[1]) - Number(a[1]))[0]?.[0];

  const refreshQuality = async () => {
    const quality = await api("/data-quality");
    setExtras((x) => ({ ...x, quality }));
  };

  const render = (id) => {
    const cardD = scopedData[id] || d,
      settings = cardSettings[`${tab}:${id}`] || {},
      cardTitle = (fallback) => settings.title?.trim() || fallback,
      scopeLabel = settings.useGlobalFilters === false
        ? [
            settings.costCenterIds?.length ? `${settings.costCenterIds.length} cost center${settings.costCenterIds.length === 1 ? "" : "s"}` : "",
            settings.accountIds?.length ? `${settings.accountIds.length} account${settings.accountIds.length === 1 ? "" : "s"}` : "",
          ].filter(Boolean).join(" · ") || "Custom scope"
        : globalCostCenterId ? (cfg?.costCenters || []).find((x) => String(x.id) === String(globalCostCenterId))?.name || "Dashboard scope" : "All data",
      cardCash = (cardD.cashflow || []).map((point) => ({ ...point, income:Number(point.income), expense:Number(point.expense) })),
      cardSources = cardD.incomeSources || {},
      cardSourceTotal = ["employee","self_employed","business_owner","investor"].reduce((sum, key) => sum + Number(cardSources[key] || 0), 0),
      cardDominantSource = Object.entries(cardSources).filter(([key]) => ["employee","self_employed","business_owner","investor"].includes(key)).sort((a,b) => Number(b[1])-Number(a[1]))[0]?.[0],
      cardBudget = cardD.budget503020 || { needs:0,wants:0,goals:0,unclassified:0 },
      cardBudgetTotal = Object.values(cardBudget).reduce((sum,value)=>sum+Number(value||0),0),
      cardPie = [["Needs",cardBudget.needs],["Wants",cardBudget.wants],["Goals",cardBudget.goals],["Unclassified",cardBudget.unclassified]].map(([name,value])=>({name,value:Number(value||0)})),
      cardSavingsRate = cardD.periodIncome > 0 ? Math.max(-999,Math.min(999,(cardD.periodNet/cardD.periodIncome)*100)) : 0;
    if (id === "balance")
      return (
        <Card className="metric-card dashboard-card">
          <span>{cardTitle("Total cash balance")}</span>
          <strong>{money(cardD.totalBalance, cardD.baseCurrency)}</strong>
          <Pill tone="green">{cardD.accounts.length} accounts</Pill>
          <small>{scopeLabel}</small>
        </Card>
      );
    if (id === "passive")
      return (
        <Card className="metric-card dashboard-card">
          <span>{cardTitle("Passive income")} · {cardD.periodLabel}</span>
          <strong>{money(cardD.passiveIncome, cardD.baseCurrency)}</strong>
          <small>Uses the selected dashboard period</small>
          <small>{scopeLabel}</small>
        </Card>
      );
    if (id === "networth")
      return (
        <Card className="metric-card dashboard-card">
          <span>{cardTitle("Net worth")}</span>
          <strong>{money(cardD.netWorth, cardD.baseCurrency)}</strong>
          <small>Cash + assets − liabilities</small>
          <small>{scopeLabel}</small>
        </Card>
      );
    if (id === "networthevolution")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>{cardTitle("Net worth evolution")} · {cardD.periodLabel}</span>
              <strong>{money(cardD.netWorth, cardD.baseCurrency)}</strong>
            </div>
          </div>
          <div className="chart-fill">
            {cardD.netWorthEvolution?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cardD.netWorthEvolution}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="d"
                    tickFormatter={(v) => String(v).slice(5)}
                  />
                  <YAxis hide />
                  <Tooltip
                    formatter={(v) => [
                      money(Number(v), cardD.baseCurrency),
                      "Net worth",
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#416da8"
                    fill="#e3ebf7"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                title="Not enough history"
                text="Add dated movements and valuations to build the curve."
              />
            )}
          </div>
          <small>
            Historical curve uses recorded cash-flow changes and the latest
            available asset valuations.
          </small>
        </Card>
      );
    if (id === "periodcomparison") {
      const c = cardD.comparison;
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>{cardTitle("Selected period comparison")}</span>
            <strong>{cardD.periodLabel}</strong>
          </div>
          {c ? (
            <div className="comparison-grid">
              <div>
                <small>Compared with</small>
                <b>{c.label}</b>
              </div>
              <div>
                <small>Net difference</small>
                <b className={c.netChange >= 0 ? "positive" : "negative"}>
                  {money(c.netChange, cardD.baseCurrency)}
                </b>
              </div>
              <div>
                <small>Percentage change</small>
                <b>
                  {c.netChangePct == null
                    ? "—"
                    : `${c.netChangePct >= 0 ? "+" : ""}${c.netChangePct.toFixed(1)}%`}
                </b>
              </div>
              <div>
                <small>Previous net cash flow</small>
                <b>{money(c.net, cardD.baseCurrency)}</b>
              </div>
            </div>
          ) : (
            <Empty
              title="No comparable earlier period"
              text="Select a month, week, year or multiple years with earlier data."
            />
          )}
        </Card>
      );
    }
    if (id === "cashflow")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>
                {cardTitle("Cash flow")} ·{" "}
                {period === "years" ? selectedYears.join(", ") : period}
              </span>
              <strong>{money(cardD.periodNet, cardD.baseCurrency)}</strong>
            </div>
            <div className="card-detail">
              <Pill tone="green">
                In {money(cardD.periodIncome, cardD.baseCurrency)}
              </Pill>
              <Pill tone="red">
                Out {money(cardD.periodExpense, cardD.baseCurrency)}
              </Pill>
            </div>
          </div>
          <div className="chart-fill">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cardCash}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="d" tickFormatter={(v) => String(v).slice(5)} />
                <YAxis hide />
                <Tooltip formatter={chartTooltip} />
                <Area
                  type="monotone"
                  dataKey="income"
                  stroke="#46a66c"
                  fill="#dff5e5"
                />
                <Area
                  type="monotone"
                  dataKey="expense"
                  stroke="#d25d57"
                  fill="#fae5e1"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      );
    if (id === "rd")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Rich Dad cash-flow view</span>
          </div>
          <div className="flow-boxes">
            <div className="flow income">
              Income
              <br />
              <b>{money(d.periodIncome, d.baseCurrency)}</b>
            </div>
            <div className="arrow">→</div>
            <div className="flow assets">
              Assets
              <br />
              <b>{money(d.assetValue, d.baseCurrency)}</b>
            </div>
            <div className="flow expense">
              Expenses
              <br />
              <b>{money(d.periodExpense, d.baseCurrency)}</b>
            </div>
          </div>
        </Card>
      );
    if (id === "incomequadrant")
      return (
        <Card className="fill dashboard-card income-quadrant-card">
          <div className="card-title">
            <div>
              <span>{cardTitle("Where your income comes from")}</span>
              <small>{cardD.periodLabel} · realised income only</small>
            </div>
          </div>
          <div className="income-quadrant">
            {[
              ["employee", "Employee"],
              ["self_employed", "Self-employed"],
              ["business_owner", "Business owner"],
              ["investor", "Investor"],
            ].map(([key, label]) => {
              const value = Number(cardSources[key] || 0),
                pct = cardSourceTotal ? (value / cardSourceTotal) * 100 : 0;
              return (
                <button
                  type="button"
                  key={key}
                  className={`income-source ${cardDominantSource === key ? "dominant" : ""}`}
                  onClick={() => navigate(`/movements?incomeSource=${key}`)}
                >
                  <span>{label}</span>
                  <b>{money(value, cardD.baseCurrency)}</b>
                  <small>
                    {pct.toFixed(0)}%
                    {cardDominantSource === key ? " · You are here" : ""}
                  </small>
                  {key === "business_owner" &&
                    Number(cardD.incomeSourceContext?.businessAssetValue || 0) >
                      0 && (
                      <em>
                        Owned business value: {money(
                          cardD.incomeSourceContext.businessAssetValue,
                          cardD.baseCurrency,
                        )}
                      </em>
                    )}
                </button>
              );
            })}
          </div>
          {Number(cardSources.unclassified || 0) > 0 && (
            <button
              type="button"
              className="income-review-link"
              onClick={() =>
                navigate("/movements?incomeSource=unclassified&reviewIncome=1")
              }
            >
              Review {Number(cardD.incomeSourceReview?.count || 0)} unclassified
              income movements · {money(cardSources.unclassified, cardD.baseCurrency)}
            </button>
          )}
          <small className="muted income-source-note">
            Transfers, asset purchases and sales, balances and asset values are
            excluded from these percentages. Business value is context only.
          </small>
        </Card>
      );
    if (id === "budget")
      return (
        <Card className="fill budget-card dashboard-card">
          <div className="card-title">
            <div>
              <span>{cardTitle("50 / 30 / 20")} · expenses only</span>
              <strong>Target vs current</strong>
            </div>
          </div>
          <div className="budget-body">
            <div className="budget-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={cardPie}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="58%"
                    outerRadius="82%"
                    paddingAngle={2}
                  >
                    {cardPie.map((_, i) => (
                      <Cell key={i} className={`pie-segment pie-${i}`} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [money(v, cardD.baseCurrency), n]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="budget-copy card-detail">
              <div className="target-row">
                <Pill>50% Needs</Pill>
                <Pill>30% Wants</Pill>
                <Pill>20% Goals</Pill>
              </div>
              <p>{advice(cardBudget, cardBudgetTotal)}</p>
              <div className="budget-values">
                <span>
                  Needs <b>{cardBudgetTotal ? Math.round((cardBudget.needs / cardBudgetTotal) * 100) : 0}%</b>
                </span>
                <span>
                  Wants <b>{cardBudgetTotal ? Math.round((cardBudget.wants / cardBudgetTotal) * 100) : 0}%</b>
                </span>
                <span>
                  Goals <b>{cardBudgetTotal ? Math.round((cardBudget.goals / cardBudgetTotal) * 100) : 0}%</b>
                </span>
                <span>
                  Unclassified{" "}
                  <b>{bt ? Math.round((b.unclassified / bt) * 100) : 0}%</b>
                </span>
              </div>
            </div>
          </div>
        </Card>
      );
    if (id === "fx")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Currency rates</span>
            <strong>ECB</strong>
          </div>
          <div className="simple-list compact-list">
            {rates.map((r) => (
              <div key={r.currency}>
                <span>1 EUR</span>
                <b>
                  {Number(r.eur_rate).toFixed(4)} {r.currency}
                </b>
              </div>
            ))}
          </div>
        </Card>
      );
    if (id === "accounts")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Accounts</span>
            <strong>Balances</strong>
          </div>
          <div className="account-mini">
            {d.accounts.slice(0, 8).map((a) => (
              <div key={a.id}>
                <span>
                  {a.name}
                  <small>{a.currency}</small>
                </span>
                <b>{money(a.current_balance, a.currency)}</b>
              </div>
            ))}
          </div>
        </Card>
      );
    if (id === "accountspotlight")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>Individual account</span>
              <small>Native and reporting-currency balance</small>
            </div>
            <select
              value={selectedAccount?.id || ""}
              onChange={(e) => setAccountCardId(e.target.value)}
            >
              {(d.accounts || []).map((a) => (
                <option value={a.id} key={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          {!selectedAccount ? (
            <Empty title="No included accounts" />
          ) : (
            <div className="comparison-grid">
              <div>
                <small>Account</small>
                <b>{selectedAccount.name}</b>
              </div>
              <div>
                <small>Place</small>
                <b>{selectedAccount.location_name || "No place"}</b>
              </div>
              <div>
                <small>Account currency</small>
                <b>
                  {money(
                    selectedAccount.current_balance,
                    selectedAccount.currency,
                  )}
                </b>
              </div>
              <div>
                <small>In {d.baseCurrency}</small>
                <b>
                  {selectedAccount.reporting_balance == null
                    ? "Rate unavailable"
                    : money(selectedAccount.reporting_balance, d.baseCurrency)}
                </b>
              </div>
              <div>
                <small>Type</small>
                <b>
                  {String(selectedAccount.account_type || "other").replaceAll(
                    "_",
                    " ",
                  )}
                </b>
              </div>
              <div>
                <small>Totals</small>
                <Pill tone="green">Included</Pill>
              </div>
            </div>
          )}
        </Card>
      );
    if (id === "placesbalances")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>Places overview</span>
              <small>Accounts grouped by bank, wallet or place</small>
            </div>
            <select
              value={placeCardName}
              onChange={(e) => setPlaceCardName(e.target.value)}
            >
              <option value="all">All places</option>
              {(d.places || []).map((place) => (
                <option value={place.name} key={place.name}>
                  {place.name}
                </option>
              ))}
            </select>
          </div>
          <div className="simple-list">
            {(selectedPlace ? [selectedPlace] : d.places || []).map((place) => (
              <div key={place.name}>
                <span>
                  <b>{place.name}</b>
                  <small>
                    {place.accountCount} account
                    {place.accountCount === 1 ? "" : "s"} ·{" "}
                    {Object.entries(place.nativeTotals || {})
                      .map(([currency, value]) => money(value, currency))
                      .join(" / ")}
                  </small>
                </span>
                <strong>{money(place.reportingTotal, d.baseCurrency)}</strong>
              </div>
            ))}
          </div>
        </Card>
      );
    if (id === "balanceevolution")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>Balance evolution · {d.periodLabel}</span>
              <small>Balances shown in {d.baseCurrency}</small>
            </div>
            <select
              value={balanceAccountId}
              onChange={(e) => setBalanceAccountId(e.target.value)}
            >
              <option value="all">All included accounts</option>
              {(d.balanceHistory?.accounts || []).map((a) => (
                <option value={a.id} key={a.id}>
                  {a.name} · {a.currency}
                </option>
              ))}
            </select>
          </div>
          <div className="chart-fill">
            {historySeries.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historySeries}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="d"
                    tickFormatter={(v) => String(v).slice(5)}
                  />
                  <YAxis hide />
                  <Tooltip
                    formatter={(v) => [
                      money(Number(v), d.baseCurrency),
                      "Balance",
                    ]}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="value"
                    stroke="#477d55"
                    fill="#e4f2e5"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                title="No balance changes in this period"
                text="Choose another account or a longer period."
              />
            )}
          </div>
        </Card>
      );
    if (id === "currencybalances")
      return (
        <Card className="fill dashboard-card currency-balances-card">
          <div className="card-title">
            <div>
              <span>Balances by currency</span>
              <small
                title={
                  fx?.date
                    ? `Latest available rates: ${fx.date}`
                    : "Latest available exchange rates"
                }
              >
                Native totals and reporting-currency equivalents
              </small>
            </div>
            <strong>{d.baseCurrency}</strong>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Currency</th>
                  <th className="right">Native total</th>
                  <th className="right">In {d.baseCurrency}</th>
                </tr>
              </thead>
              <tbody>
                {currencyRows.map((row) => (
                  <tr
                    key={row.currency}
                    className="clickable"
                    title={`Open ${row.currency} accounts`}
                    onClick={() =>
                      navigate(`/accounts?currency=${row.currency}`)
                    }
                  >
                    <td>
                      <b>{row.currency}</b>
                    </td>
                    <td className="right">{money(row.native, row.currency)}</td>
                    <td className="right">
                      {row.converted == null
                        ? "Rate unavailable"
                        : money(row.converted, d.baseCurrency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2">
                    <b>Total account balance</b>
                  </td>
                  <td className="right">
                    <b>{money(d.totalBalance, d.baseCurrency)}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      );
    if (id === "comparison") {
      const c = cardD.comparison;
      return (
        <Card className="metric-card dashboard-card">
          <span>{c?.label || "Previous period"}</span>
          {c ? (
            <>
              <strong className={c.netChange >= 0 ? "positive" : "negative"}>
                {c.netChange >= 0 ? <TrendingUp /> : <TrendingDown />}{" "}
                {money(c.netChange, cardD.baseCurrency)}
              </strong>
              <small>
                {c.netChangePct == null
                  ? "No percentage baseline"
                  : `${c.netChangePct >= 0 ? "+" : ""}${c.netChangePct.toFixed(1)}% net cash-flow change`}
              </small>
            </>
          ) : (
            <>
              <strong>—</strong>
              <small>Comparison is not available for All time.</small>
            </>
          )}
        </Card>
      );
    }
    if (id === "projects")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>{cardTitle("Projects")} · {cardD.periodLabel}</span>
            <strong>Selected-period totals</strong>
          </div>
          {!cardD.projects?.length ? (
            <Empty title="No projects yet" />
          ) : (
            <div className="simple-list">
              {cardD.projects.slice(0, 5).map((project) => (
                <div key={project.id}>
                  <span>
                    <b>{project.name}</b>
                    <small>{project.project_type}</small>
                  </span>
                  <strong>{money(project.total, cardD.baseCurrency)}</strong>
                </div>
              ))}
            </div>
          )}
        </Card>
      );
    if (id === "budgets")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Budgets</span>
            <strong>{extras.budgets?.length || 0}</strong>
          </div>
          <div className="simple-list">
            {(extras.budgets || []).slice(0, 5).map((x) => (
              <div key={x.id}>
                <span>{x.name}</span>
                <b>{money(x.amount, x.currency)}</b>
              </div>
            ))}
          </div>
        </Card>
      );
    if (id === "goals")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Goals</span>
            <strong>{extras.goals?.length || 0}</strong>
          </div>
          <div className="simple-list">
            {(extras.goals || []).slice(0, 5).map((x) => (
              <div key={x.id}>
                <span>{x.name}</span>
                <b>
                  {money(x.current_amount, x.currency)} /{" "}
                  {money(x.target_amount, x.currency)}
                </b>
              </div>
            ))}
          </div>
        </Card>
      );
    if (id === "subscriptions")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Subscriptions</span>
            <strong>
              {(extras.subs || []).filter((x) => x.status === "active").length}{" "}
              active
            </strong>
          </div>
          <div className="simple-list">
            {(extras.subs || []).slice(0, 5).map((x) => (
              <div key={x.id}>
                <span>{x.name}</span>
                <Pill>{x.status}</Pill>
              </div>
            ))}
          </div>
        </Card>
      );
    if (id === "investments") {
      const total = (extras.assets || []).reduce(
        (sum, x) =>
          sum +
          Number(
            x.is_liability
              ? -Math.abs(x.current_value || 0)
              : x.current_value || 0,
          ),
        0,
      );
      return (
        <Card className="metric-card dashboard-card">
          <span>Investment / asset value</span>
          <strong>{money(total, d.baseCurrency)}</strong>
          <small>{extras.assets?.length || 0} tracked assets</small>
        </Card>
      );
    }
    if (id === "assetmix")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>Assets by type</span>
              <small>Current values in {d.baseCurrency}</small>
            </div>
            <strong>{extras.assets?.length || 0} assets</strong>
          </div>
          {assetGroups.length ? (
            <div className="budget-body">
              <div className="budget-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={assetGroups}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="55%"
                      outerRadius="82%"
                      paddingAngle={2}
                    >
                      {assetGroups.map((_, i) => (
                        <Cell key={i} className={`pie-segment pie-${i % 4}`} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [
                        money(Number(v), d.baseCurrency),
                        n,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="simple-list compact-list">
                {assetGroups.slice(0, 6).map((group) => (
                  <div key={group.name}>
                    <span>
                      {group.name}
                      <small>
                        {group.count} item{group.count === 1 ? "" : "s"}
                      </small>
                    </span>
                    <b>{money(group.value, d.baseCurrency)}</b>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Empty title="No assets yet" />
          )}
        </Card>
      );
    if (id === "assetshowcase")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <div>
              <span>Asset showcase</span>
              <small>Property, stocks, bonds and other holdings</small>
            </div>
            <select
              value={assetTypeFilter}
              onChange={(e) => setAssetTypeFilter(e.target.value)}
            >
              <option value="all">All asset types</option>
              {assetTypes.map((type) => (
                <option value={type} key={type}>
                  {type.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </div>
          {!showcaseAssets.length ? (
            <Empty title="No assets in this group" />
          ) : (
            <div className="simple-list">
              {showcaseAssets.slice(0, 6).map((asset) => (
                <div
                  className="clickable"
                  key={asset.id}
                  onClick={() => navigate("/investments")}
                >
                  <span>
                    <b>{asset.name}</b>
                    <small>
                      {String(asset.asset_type || "other").replaceAll("_", " ")}{" "}
                      · {asset.quantity || 1} units · {asset.currency}
                    </small>
                  </span>
                  <strong>
                    {money(
                      asset.current_value ??
                        Number(asset.quantity || 1) *
                          Number(asset.current_price || 0),
                      asset.currency,
                    )}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </Card>
      );
    if (id === "review")
      return (
        <Card className="metric-card dashboard-card">
          <span>Movements needing review</span>
          <strong>{d.reviewCount || 0}</strong>
          <small>{d.unclassifiedCount || 0} expenses missing 50/30/20</small>
          <button
            className="text-button"
            onClick={() => navigate("/movements")}
          >
            Review movements
          </button>
        </Card>
      );
    if (id === "quality") {
      const q = extras.quality;
      return (
        <Card className="fill dashboard-card quality-card">
          <div className="card-title">
            <span>Data quality</span>
            <strong>{q ? `${q.score}% organized` : "—"}</strong>
          </div>
          {!q ? (
            <div className="loading compact">Reviewing…</div>
          ) : (
            <>
              <div className="quality-grid">
                <span>
                  <b>{q.notCleared}</b>
                  <small>Not cleared</small>
                </span>
                <span>
                  <b>{q.uncategorized}</b>
                  <small>Uncategorized</small>
                </span>
                <span>
                  <b>{q.budgetUnclassified}</b>
                  <small>50/30/20 missing</small>
                </span>
                <span>
                  <b>{q.similarCategories?.length || 0}</b>
                  <small>Similar category pairs</small>
                </span>
              </div>
              {q.similarCategories?.[0] && (
                <small>
                  Possible cleanup: “{q.similarCategories[0].a.custom_name}” and
                  “{q.similarCategories[0].b.custom_name}”.
                </small>
              )}
              <div className="quality-actions">
                <button
                  className="secondary compact"
                  title="Recalculate data-quality checks"
                  onClick={refreshQuality}
                >
                  <ShieldCheck size={14} /> Run review
                </button>
                <button
                  className="text-button"
                  onClick={() => navigate("/movements")}
                >
                  <ScanSearch size={14} /> Fix movements
                </button>
              </div>
            </>
          )}
        </Card>
      );
    }
    if (id === "savingsrate")
      return (
        <Card className="metric-card dashboard-card">
          <span>{cardTitle("Savings rate")}</span>
          <strong>{cardSavingsRate.toFixed(1)}%</strong>
          <small>Net cash flow ÷ income for this period</small>
        </Card>
      );
    if (id === "incomeexpense")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>{cardTitle("Income vs expenses")}</span>
          </div>
          <div className="chart-fill">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  {
                    name: "This period",
                    income: cardD.periodIncome,
                    expense: cardD.periodExpense,
                  },
                ]}
              >
                <XAxis dataKey="name" />
                <YAxis hide />
                <Tooltip formatter={chartTooltip} />
                <Bar dataKey="income" fill="#8bcf9b" />
                <Bar dataKey="expense" fill="#e4a09b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      );
    if (id === "reconciliation") {
      const recs = extras.recs || [],
        bad = recs.filter((x) => x.status !== "reconciled");
      return (
        <Card className="metric-card dashboard-card">
          <span>Reconciliation</span>
          <strong>{bad.length ? `${bad.length} to review` : "All good"}</strong>
          <small>{recs.length} reconciliation checks recorded</small>
        </Card>
      );
    }
    if (id === "recent")
      return (
        <Card className="fill dashboard-card">
          <div className="card-title">
            <span>Recent movements</span>
          </div>
          <div className="simple-list">
            {(extras.recent || []).slice(0, 6).map((x) => (
              <div key={`${x.account_id}-${x.id}`}>
                <span>
                  <b>{x.description || "—"}</b>
                  <small>
                    {dateFmt(x.operation_date)} · {x.account_name}
                  </small>
                </span>
                <strong>{money(x.amount, x.currency)}</strong>
              </div>
            ))}
          </div>
        </Card>
      );
    return (
      <Card>
        <Empty title={id} />
      </Card>
    );
  };

  const autoArrange = () =>
    setLayouts((x) => ({ ...x, [tab]: buildLayouts(currentCards) }));
  const addCard = (id) => {
    if (currentCards.includes(id)) return;
    const next = [...currentCards, id];
    setCardsByTab((x) => ({ ...x, [tab]: next }));
    setLayouts((x) => ({ ...x, [tab]: buildLayouts(next) }));
  };
  const hideCard = (id) => {
    const next = currentCards.filter((k) => k !== id);
    setCardsByTab((x) => ({ ...x, [tab]: next }));
    setLayouts((x) => ({ ...x, [tab]: buildLayouts(next) }));
  };
  const openCardConfig = (id) => {
    const savedSettings = cardSettings[`${tab}:${id}`] || {};
    setCardConfig({ id, title:savedSettings.title || "", useGlobalFilters:savedSettings.useGlobalFilters !== false, accountIds:savedSettings.accountIds || [], costCenterIds:savedSettings.costCenterIds || [] });
  };
  const saveCardConfig = (event) => {
    event.preventDefault();
    setCardSettings((current) => ({ ...current, [`${tab}:${cardConfig.id}`]:{ title:cardConfig.title.trim(), useGlobalFilters:cardConfig.useGlobalFilters, accountIds:cardConfig.accountIds.map(Number), costCenterIds:cardConfig.costCenterIds.map(Number) } }));
    setCardConfig(null);
  };

  return (
    <>
      <div className="page-head dashboard-head">
        <div>
          <h1>Good morning, {user.name?.split(" ")[0]}</h1>
          <p>A clear view of where your money is moving.</p>
        </div>
        <div className="actions">
          <button
            className="primary"
            onClick={() => onAddMovement?.({ source: "dashboard" })}
          >
            <PlusCircle size={17} /> {t(user.locale, "addMovement")}
          </button>
          <button
            className={edit ? "primary" : "secondary"}
            onClick={() => setEdit(!edit)}
          >
            {edit ? <Check size={17} /> : <Pencil size={17} />}{" "}
            {edit ? "Done" : "Edit dashboard"}
          </button>
        </div>
      </div>
      <div className="dashboard-tabs-row">
        <div className="tabs">
          {tabs.map((name) => (
            <button
              className={tab === name ? "active" : ""}
              onClick={() => setTab(name)}
              key={name}
            >
              {name}
            </button>
          ))}
          <button onClick={addTab}>
            <Plus size={15} /> Tab
          </button>
        </div>
        <div className="dashboard-period">
          <label className="dashboard-cost-center-filter">
            <span>Cost center</span>
            <select value={globalCostCenterId} onChange={(event) => setGlobalCostCenterId(event.target.value)}>
              <option value="">All cost centers</option>
              {(cfg?.costCenters || []).filter((center) => center.is_active !== 0).map((center) => <option key={center.id} value={center.id}>{center.name}</option>)}
            </select>
          </label>
          <button className="icon" title="Make this the default dashboard view" aria-label="Make this the default dashboard view" onClick={saveDefault}>
            <Pin size={17} />
          </button>
          <button className="icon" title="Reset standard dashboard view" aria-label="Reset standard dashboard view" onClick={resetDefault}>
            <RotateCcw size={17} />
          </button>
          <select
            className="period-select"
            value={period}
            onChange={(e) => {
              const value = e.target.value;
              setPeriod(value);
              if (value === "years" && !selectedYears.length)
                setSelectedYears((d.availableYears || []).slice(0, 1));
            }}
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Last 12 months</option>
            <option value="ytd">This year · YTD</option>
            <option value="all">All time</option>
            <option value="years">Select years</option>
          </select>
          {period === "years" && (
            <div className="period-choices">
              {(d.availableYears || []).map((year) => (
                <button
                  type="button"
                  className={
                    selectedYears.includes(year)
                      ? "filter-pill active"
                      : "filter-pill"
                  }
                  key={year}
                  onClick={() =>
                    setSelectedYears((values) =>
                      values.includes(year)
                        ? values.filter((x) => x !== year)
                        : [...values, year].sort((a, b) => b - a),
                    )
                  }
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {edit && (
        <Card className="dashboard-editor">
          <div className="dashboard-editor-toolbar">
            <div>
              <h3>Dashboard layout</h3>
              <small>
                Add, hide, resize or automatically pack the cards on this tab.
              </small>
            </div>
            <button className="secondary compact" onClick={autoArrange}>
              <Wand2 size={15} /> Auto arrange tightly
            </button>
          </div>
          <div>
            <h3>Visible cards</h3>
            <div className="card-library">
              {currentCards.map((id) => (
                <button key={id} className="library-item visible">
                  <Eye size={14} />
                  {meta[id]?.title || id}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      hideCard(id);
                    }}
                  >
                    Hide
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3>Available cards</h3>
            <div className="card-library">
              {Object.keys(meta)
                .filter((id) => !currentCards.includes(id))
                .map((id) => (
                  <button
                    key={id}
                    className="library-item"
                    onClick={() => addCard(id)}
                  >
                    <Plus size={14} />
                    {meta[id].title}
                  </button>
                ))}
            </div>
          </div>
        </Card>
      )}

      {!currentCards.length ? (
        <Card>
          <Empty
            title="This dashboard tab is empty"
            text="Choose Edit dashboard and add cards from the library."
          />
        </Card>
      ) : (
        <Grid
          className="layout"
          layouts={currentLayouts}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={190}
          compactType="vertical"
          preventCollision={false}
          isDraggable={edit}
          isResizable={edit}
          draggableHandle=".dashboard-card-handle"
          onLayoutChange={(current, all) =>
            setLayouts((x) => ({ ...x, [tab]: all }))
          }
        >
          {currentCards.map((id) => (
            <div key={id}>
              <div className="dashboard-card-shell">
                {configurableCards.has(id) && (
                  <button className="icon dashboard-card-settings" title="Configure this card instance" aria-label={`Configure ${meta[id]?.title || id}`} onMouseDown={(event) => event.stopPropagation()} onClick={() => openCardConfig(id)}>
                    <Settings2 size={15}/>
                  </button>
                )}
                {edit && (
                  <div className="dashboard-card-handle">
                    <GripVertical size={15} />
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => hideCard(id)}
                    >
                      Hide
                    </button>
                  </div>
                )}
                {render(id)}
              </div>
            </div>
          ))}
        </Grid>
      )}
      {cardConfig && (
        <Modal title={`Configure ${meta[cardConfig.id]?.title || "card"}`} screenId="D0208" onClose={() => setCardConfig(null)}>
          <form className="form-grid" onSubmit={saveCardConfig}>
            <label className="span2">Card title<input value={cardConfig.title} onChange={(event) => setCardConfig({ ...cardConfig, title:event.target.value })} placeholder={meta[cardConfig.id]?.title}/></label>
            <label className="check span2"><input type="checkbox" checked={cardConfig.useGlobalFilters} onChange={(event) => setCardConfig({ ...cardConfig, useGlobalFilters:event.target.checked })}/> Follow dashboard-wide cost-center filter</label>
            {!cardConfig.useGlobalFilters && <><label>Cost centers<select multiple value={cardConfig.costCenterIds.map(String)} onChange={(event) => setCardConfig({ ...cardConfig, costCenterIds:[...event.target.selectedOptions].map((option) => Number(option.value)) })}>{(cfg?.costCenters || []).filter((center) => center.is_active !== 0).map((center) => <option value={center.id} key={center.id}>{center.name}</option>)}</select><small>Hold Ctrl/Cmd to choose several.</small></label><label>Accounts<select multiple value={cardConfig.accountIds.map(String)} onChange={(event) => setCardConfig({ ...cardConfig, accountIds:[...event.target.selectedOptions].map((option) => Number(option.value)) })}>{(cfg?.accounts || []).filter((account) => account.is_active !== 0).map((account) => <option value={account.id} key={account.id}>{account.name} · {account.currency}</option>)}</select><small>Leave empty to include every account in the selected cost centers.</small></label></>}
            <div className="modal-actions span2"><button type="button" className="secondary" onClick={() => { setCardSettings((current) => { const next={...current}; delete next[`${tab}:${cardConfig.id}`]; return next; }); setCardConfig(null); }}>Reset card</button><button className="primary">Save card settings</button></div>
          </form>
        </Modal>
      )}
    </>
  );
}
